import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input } from '@angular/core';
import { IWidgetItem } from '../../interfaces/widget-item.interface';
import { FormGroup } from '@angular/forms';
import { EMPTY, Observable, Subject } from 'rxjs';
import { get, isEmpty, isEqual } from 'lodash';
import { IDynamicFormPayload, IdynamicFormItem } from '@ispm/shared/dynamic-form/interfaces/IdynamicFormItem';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { BundleCall } from '@ispm-client/modules/aggregator/services';
import { ResolvableComponent } from '@ispm/core/decorators/resolvable-component.decorator';
import { map } from 'rxjs/operators';

// Additional context constants
const CANCER_TYPE = 'cancerType';

@Component({
  selector: 'ispm-dynamic-context-form',
  templateUrl: './dynamic-context-form.component.html',
  styleUrls: ['./dynamic-context-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@ResolvableComponent()
export class DynamicContextFormComponent implements IWidgetItem<IDynamicFormPayload> , OnInit{

  public initChange: boolean = true;
  public viewInit: boolean = false;
  public itemName: string;
  public widgetFormInstance: FormGroup;
  public valid: boolean = true;
  public disabled: any;
  public conditionReference: string = '';
  @Input() payload: any;
  public widgetUpdateValueCallback: (value) => void;
  public formData: any =[];
  private destroyer: Subject<any> = new Subject();
  private currentContextValue: string = '';
  public hiddenCount: number = 0;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private genomicsStore: Store<IgenomicsState>,
              private _bundleCall: BundleCall
    ) {
      this.genomicsStore.select('tests').take(1).subscribe(data => {
        if (data && data.diagnosticOrders.order.condition) {
          this.conditionReference = data.diagnosticOrders.order.condition.reference;
        }
      });
    }

  /**
   * Anguar life cycle hook
   */
  public ngOnInit(): void {
      this.getsCurrentContextValue().subscribe();
  }

  /**
   * Gets current context
   */
  private getsCurrentContextValue(): Observable<any> {
    if(!this.currentContextValue) {
        return this.getContextValueObservable();
    }
  }

  /**
   * Gets context value observable
   * @returns Observable of context value
   */
  private getContextValueObservable(): Observable<any> {
    // Can be extended for other context types as well hence keeping it as switch
    switch(this.payload.context) {
      case CANCER_TYPE: {
        return this._bundleCall.searchCall(this.getCurrentCancerType()).pipe(map((data) => {
          this.currentContextValue = data[0].response.entry[0].resource.cancerType;
          return data[0].response.entry[0].resource.cancerType;
        }));
      }
      default: {
        return EMPTY;
      }
    }
  }

  /**
   * Fetches current cancer type
   */
  private getCurrentCancerType() {
    return {
      'queries': [{
        'targetResource': 'Condition',
        'queryString': `_id=${this.conditionReference}&subResourceTypeCode=primary_cancer&categoryCode=primary&_fieldslist=cancerType&_count=1`,
      }]
    }
  }

  /**
   * Invoke during initialization and  sets the widget data if any
   */
  set widgetData(widgetData: any) {
    if (this.payload.context) {
      // Skips if value is already present improves API calls time if any
      if(!this.currentContextValue) {
        this.getsCurrentContextValue().subscribe(() => {
            widgetData = widgetData ?? [];
            this.formData = (!isEmpty(widgetData)) ? this.getContextSpecificFilterData(widgetData) : [];
            if(this.payload.mode === 'view') {
              this.hiddenCount = this.formData.length - this.payload.showCount;
              this.formData = !isEmpty(this.formData) ? this.formData.slice(0,1) : [{}];
            }
            this.markedFormDataChange();
        });
      }
      else {
          widgetData = widgetData ?? [];
          this.formData = (!isEmpty(widgetData)) ? this.getContextSpecificFilterData(widgetData) : [];
          if(this.payload.mode === 'view') {
            this.hiddenCount = this.formData.length - this.payload.showCount;
            this.formData = !isEmpty(this.formData) ? this.formData.slice(0,1) : [{}];
          }
          this.markedFormDataChange();
        }
      }
  }

  /**
   * Filters and return context specific data
   * @param item holds original data
   * @returns filtered data
   */
  private getContextSpecificFilterData(item) {
    if(!this.payload.takeContextValueOnly) {
      return item.filter((data)=> {
        return (!data[`${this.payload.context}`]) || isEqual(data[`${this.payload.context}`], this.currentContextValue);
      });
    }
    else return item.filter((data) => isEqual(data[`${this.payload.context}`], this.currentContextValue) );
  }

  /**
   * invoke at the initialization and sets the widget cofiguration
   */
  set widgetConfig(config: IdynamicFormItem) {
    this.payload = config.widgetPayload;
    this.itemName = config.name;
    this.payload = Object.assign({}, this.payload, { viewAsTable: get(this.payload, 'viewAsTable', true)});
    this.payload = Object.assign({}, this.payload, { tableVertical: get(this.payload, 'tableVertical', true)});
    this.payload = Object.assign({}, this.payload, { formVertical: get(this.payload, 'formVertical', false)});
    this.payload = Object.assign({}, this.payload, { context: get(this.payload, 'context', null)});
    this.payload = Object.assign({}, this.payload, { takeContextValueOnly: get(this.payload, 'takeContextValueOnly', false)});
    if (this.widgetFormInstance && !this.payload.disabled) {
      this.disabled = get(this.widgetFormInstance, 'disabled', false);
    }
  }

  /**
   * Invokes on delete of any row
   * @param deleteData holds deleted item info
   */
  public deleteItem(deleteData) {
    if(deleteData && deleteData.item) {
      deleteData.item[this.payload.deleteFieldModelName] = deleteData.item[this.payload.deleteFieldModelName] ? null : true;
      deleteData.item['_df_modified'] = true;
    }
  }

  /**
   * Allows Item to be deletable
   * @returns boolean value for item being deletable
   */
  public isItemDeletable(): boolean {
    return true;
  }

  /**
   * indicate if to remove an item from the list, when Delete action is activated
   * @param itemDetails holds item value
   * @returns {boolean} to allow delete
   */
  public shouldRemoveItemOnDelete(itemDetails) {
    return (itemDetails && isEmpty(itemDetails['id']));
  }

  /**
   * Returns css class on condition
   * @param itemDetails holds individual row details
   * @returns 
   */
  public getItemCss(itemDetails) {
    const bool: boolean = itemDetails[this.payload.deleteFieldModelName] ? true : false;
    return { 'diff_marker': bool, 'dynamic-form-item-deleted': bool};
  }

  /**
   * Invokes on adding a new field/row
   */
  public addContextToNewItem(item) {
    if(this.payload.context && item) {
      if(!this.currentContextValue) {
        this.getsCurrentContextValue().subscribe(() => {
          this.formData = item.dataArray ? this.addNewItemWithContext(item.dataArray, item.dataArray.length - 1) : [];
        });
      }
      else {
          this.formData = item.dataArray ? this.addNewItemWithContext(item.dataArray, item.dataArray.length - 1) : [];
      }
    }
  }

  /**
   * Adds new item with context as key
   * @param item original items to add
   * @param lastElementIndex last element index
   */
  private addNewItemWithContext(item, lastElementIndex) {
    return item.map((data, index) => {
      let contextKey: string = this.payload.context;
      if (index === lastElementIndex) {
        data[`${contextKey}`] = this.currentContextValue;
      }
      return data;
  });
  }

  /**
   * function to emit the Updated data
   * @param updatedData updated data
   */
  public handleUpdateEvent(updatedData) {
    this.formData = updatedData ?? [];
    this.markedFormDataChange();
  }

  /**
   * Emits data to parent
   */
  private emitData() {
    if (this.widgetUpdateValueCallback) {
      this.widgetUpdateValueCallback.apply(this, [this.formData]);
    }
  }
  
  /**
   * Marked for change after any form update
   */
  private markedFormDataChange() {
    this.emitData();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
    this.viewInit = true;
  }

  /**
   * Angular life cycle hook
   */
  public ngOnDestroy(): void {
    this.destroyer.next();
    this.destroyer.complete();
  }

  /**
   * This the callback function used for the validation in the async validation
   * @returns {boolean}
   */
  widgetValidator(): boolean {
    return  this.valid;
  }

}
